import React, { useState, useEffect } from 'react';
import { BuildingStorefrontIcon, ShieldCheckIcon, PrinterIcon, UsersIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import CustomSelect from '../../components/ui/CustomSelect';

const POS_SETTINGS_KEY = 'pos_printer_settings';

interface PrinterSettings {
  paperSize: '80mm' | '58mm';
  autoPrint: boolean;
  storeName: string;
  storeAddress: string;
  storePhone: string;
  footerMessage: string;
  showLogo: boolean;
  copies: number;
}

const defaultPrinterSettings: PrinterSettings = {
  paperSize: '80mm',
  autoPrint: false,
  storeName: 'SIGNATURE STYLES',
  storeAddress: 'Yangon, Myanmar',
  storePhone: '(+95)9943000730',
  footerMessage: 'Thank you for your purchase!',
  showLogo: false,
  copies: 1,
};

const AdminSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('store');
  const [printerSettings, setPrinterSettings] = useState<PrinterSettings>(defaultPrinterSettings);
  const [printerSaved, setPrinterSaved] = useState(false);

  // Staff management state
  const [staffUsers, setStaffUsers] = useState<any[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [staffForm, setStaffForm] = useState({ name: '', email: '', password: '', phone: '', role: 'pos_cashier' });

  const tabs = [
    { id: 'store', name: 'Store Info', icon: BuildingStorefrontIcon },
    { id: 'staff', name: 'Staff', icon: UsersIcon },
    { id: 'pos_printer', name: 'POS Printer', icon: PrinterIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon },
  ];

  // Load printer settings from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(POS_SETTINGS_KEY);
      if (saved) {
        setPrinterSettings({ ...defaultPrinterSettings, ...JSON.parse(saved) });
      }
    } catch {}
  }, []);

  // Load staff users when tab is active
  useEffect(() => {
    if (activeTab === 'staff') {
      fetchStaff();
    }
  }, [activeTab]);

  const fetchStaff = async () => {
    setStaffLoading(true);
    try {
      // Only fetch non-customer users (staff roles)
      const roles = ['super_admin', 'pos_admin', 'pos_cashier', 'product_manager', 'order_manager'];
      const allStaff: any[] = [];
      for (const role of roles) {
        const response = await apiService.get('/admin/users', { params: { role, per_page: 50 } });
        const users = response.data || [];
        users.forEach((u: any) => {
          if (!allStaff.find((s: any) => s.id === u.id)) {
            allStaff.push(u);
          }
        });
      }
      setStaffUsers(allStaff);
    } catch {}
    setStaffLoading(false);
  };

  const handleCreateStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiService.post('/admin/users', staffForm);
      setShowStaffModal(false);
      setStaffForm({ name: '', email: '', password: '', phone: '', role: 'pos_cashier' });
      fetchStaff();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create user');
    }
  };

  const handleToggleStaffStatus = async (userId: number, currentStatus: any) => {
    const isActive = currentStatus === true || currentStatus === 1 || currentStatus === '1';
    try {
      await apiService.put(`/admin/users/${userId}`, { is_active: !isActive });
      fetchStaff();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to update status');
    }
  };

  // Reset password modal state
  const [showResetPwModal, setShowResetPwModal] = useState(false);
  const [resetPwUserId, setResetPwUserId] = useState<number | null>(null);
  const [resetPwUserName, setResetPwUserName] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const openResetPwModal = (userId: number, userName: string) => {
    setResetPwUserId(userId);
    setResetPwUserName(userName);
    setNewPassword('');
    setShowResetPwModal(true);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetPwUserId || newPassword.length < 8) return;
    try {
      await apiService.put(`/admin/users/${resetPwUserId}/reset-password`, { password: newPassword });
      setShowResetPwModal(false);
      setNewPassword('');
      alert('Password reset successfully');
    } catch {
      alert('Failed to reset password');
    }
  };

  const savePrinterSettings = () => {
    localStorage.setItem(POS_SETTINGS_KEY, JSON.stringify(printerSettings));
    setPrinterSaved(true);
    setTimeout(() => setPrinterSaved(false), 2000);
  };

  const handleTestPrint = () => {
    const width = printerSettings.paperSize === '80mm' ? '80mm' : '58mm';
    const printWindow = window.open('', '_blank', 'width=320,height=400');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Test Print</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Courier New', monospace; width: ${width}; padding: 3mm; font-size: 11px; line-height: 1.3; }
            .center { text-align: center; }
            .divider { border-top: 1px dashed #000; margin: 4px 0; }
            .bold { font-weight: bold; }
            @media print { body { width: ${width}; } @page { margin: 0; size: ${width} auto; } }
          </style>
        </head>
        <body>
          <div class="center">
            <p class="bold" style="font-size:14px">${printerSettings.storeName}</p>
            <p>${printerSettings.storeAddress}</p>
            <p>Tel: ${printerSettings.storePhone}</p>
          </div>
          <div class="divider"></div>
          <p class="center bold">*** TEST PRINT ***</p>
          <div class="divider"></div>
          <p>Paper Size: ${printerSettings.paperSize}</p>
          <p>Auto Print: ${printerSettings.autoPrint ? 'ON' : 'OFF'}</p>
          <p>Copies: ${printerSettings.copies}</p>
          <p>Date: ${new Date().toLocaleString()}</p>
          <div class="divider"></div>
          <p class="center">${printerSettings.footerMessage}</p>
          <script>
            window.onload = function() { setTimeout(function() { window.print(); window.close(); }, 300); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your store settings and preferences</p>
      </div>

      <div className="bg-white rounded-lg shadow">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'store' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Store Name</label>
                <input
                  type="text"
                  defaultValue="Signature Styles"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Store Email</label>
                <input
                  type="email"
                  defaultValue="info@signaturestyles.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Store Phone</label>
                <input
                  type="tel"
                  defaultValue="+95 9 123 456 789"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Store Address</label>
                <textarea
                  rows={3}
                  defaultValue="Yangon, Myanmar"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <button className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors">
                Save Changes
              </button>
            </div>
          )}

          {activeTab === 'staff' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Staff Management</h3>
                  <p className="text-sm text-gray-500">Create and manage cashiers and staff accounts</p>
                </div>
                <button
                  onClick={() => setShowStaffModal(true)}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm"
                >
                  + Add Staff
                </button>
              </div>

              {/* Staff Table */}
              <div className="overflow-hidden border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {staffLoading ? (
                      <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500">Loading...</td></tr>
                    ) : staffUsers.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500">No staff users found</td></tr>
                    ) : (
                      staffUsers.map((user: any) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{user.email}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {user.roles?.[0]?.display_name || user.roles?.[0]?.name || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center space-x-2">
                            <button onClick={() => handleToggleStaffStatus(user.id, user.is_active)} className="text-xs text-yellow-600 hover:text-yellow-800">
                              {(user.is_active === true || user.is_active === 1 || user.is_active === '1') ? 'Deactivate' : 'Activate'}
                            </button>
                            <button onClick={() => openResetPwModal(user.id, user.name)} className="text-xs text-indigo-600 hover:text-indigo-800">
                              Reset PW
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Create Staff Modal */}
              {showStaffModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-xl">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-bold text-gray-900">Add Staff</h3>
                      <button onClick={() => setShowStaffModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
                    </div>
                    <form onSubmit={handleCreateStaff} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                        <input type="text" required value={staffForm.name} onChange={(e) => setStaffForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                        <input type="email" required value={staffForm.email} onChange={(e) => setStaffForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                        <input type="password" required minLength={8} value={staffForm.password} onChange={(e) => setStaffForm(f => ({ ...f, password: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm" placeholder="Min 8 characters" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                        <input type="text" value={staffForm.phone} onChange={(e) => setStaffForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm" />
                      </div>
                      <CustomSelect
                        label="Role *"
                        value={staffForm.role}
                        onChange={(val) => setStaffForm(f => ({ ...f, role: val }))}
                        options={[
                          { value: 'pos_cashier', label: 'POS Cashier', description: 'Can sell, hold/resume sales, view own sales' },
                          { value: 'pos_admin', label: 'POS Admin', description: 'Full POS access + inventory, suppliers, reports' },
                        ]}
                      />
                      <div className="flex justify-end space-x-3 pt-2">
                        <button type="button" onClick={() => setShowStaffModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
                        <button type="submit" className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800">Create Staff</button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Reset Password Modal */}
              {showResetPwModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg w-full max-w-sm p-6 shadow-xl">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-bold text-gray-900">Reset Password</h3>
                      <button onClick={() => setShowResetPwModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">Reset password for <strong>{resetPwUserName}</strong></p>
                    <form onSubmit={handleResetPassword} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">New Password *</label>
                        <input type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm" placeholder="Min 8 characters" />
                      </div>
                      <div className="flex justify-end space-x-3 pt-2">
                        <button type="button" onClick={() => setShowResetPwModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
                        <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">Reset Password</button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'pos_printer' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  Configure your thermal receipt printer settings. These settings are stored locally on this device.
                </p>
              </div>

              {/* Paper Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Paper Size</label>
                <div className="flex gap-3">
                  <label className={`flex-1 flex items-center justify-center px-4 py-3 rounded-lg border cursor-pointer transition ${
                    printerSettings.paperSize === '80mm' ? 'border-gray-900 bg-gray-50 ring-2 ring-gray-900' : 'border-gray-300 hover:bg-gray-50'
                  }`}>
                    <input type="radio" name="paperSize" value="80mm" checked={printerSettings.paperSize === '80mm'} onChange={() => setPrinterSettings(s => ({ ...s, paperSize: '80mm' }))} className="sr-only" />
                    <div className="text-center">
                      <p className="font-medium text-gray-900">80mm</p>
                      <p className="text-xs text-gray-500">Standard (80×80 / 80×60)</p>
                    </div>
                  </label>
                  <label className={`flex-1 flex items-center justify-center px-4 py-3 rounded-lg border cursor-pointer transition ${
                    printerSettings.paperSize === '58mm' ? 'border-gray-900 bg-gray-50 ring-2 ring-gray-900' : 'border-gray-300 hover:bg-gray-50'
                  }`}>
                    <input type="radio" name="paperSize" value="58mm" checked={printerSettings.paperSize === '58mm'} onChange={() => setPrinterSettings(s => ({ ...s, paperSize: '58mm' }))} className="sr-only" />
                    <div className="text-center">
                      <p className="font-medium text-gray-900">58mm</p>
                      <p className="text-xs text-gray-500">Compact</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Auto Print */}
              <div className="flex items-center justify-between py-3 border-b">
                <div>
                  <p className="font-medium text-gray-900">Auto Print Receipt</p>
                  <p className="text-sm text-gray-500">Automatically print receipt after completing a sale</p>
                </div>
                <input
                  type="checkbox"
                  checked={printerSettings.autoPrint}
                  onChange={(e) => setPrinterSettings(s => ({ ...s, autoPrint: e.target.checked }))}
                  className="w-5 h-5 text-gray-900 rounded focus:ring-gray-900"
                />
              </div>

              {/* Copies */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Number of Copies</label>
                <select
                  value={printerSettings.copies}
                  onChange={(e) => setPrinterSettings(s => ({ ...s, copies: Number(e.target.value) }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                >
                  <option value={1}>1 Copy</option>
                  <option value={2}>2 Copies</option>
                  <option value={3}>3 Copies</option>
                </select>
              </div>

              {/* Receipt Header */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Receipt Store Name</label>
                <input
                  type="text"
                  value={printerSettings.storeName}
                  onChange={(e) => setPrinterSettings(s => ({ ...s, storeName: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Receipt Address</label>
                <input
                  type="text"
                  value={printerSettings.storeAddress}
                  onChange={(e) => setPrinterSettings(s => ({ ...s, storeAddress: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Receipt Phone</label>
                <input
                  type="text"
                  value={printerSettings.storePhone}
                  onChange={(e) => setPrinterSettings(s => ({ ...s, storePhone: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Footer Message</label>
                <input
                  type="text"
                  value={printerSettings.footerMessage}
                  onChange={(e) => setPrinterSettings(s => ({ ...s, footerMessage: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Thank you for your purchase!"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={savePrinterSettings}
                  className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  {printerSaved ? '✓ Saved!' : 'Save Settings'}
                </button>
                <button
                  onClick={handleTestPrint}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <PrinterIcon className="w-4 h-4" />
                  Test Print
                </button>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                <input
                  type="password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <input
                  type="password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                <input
                  type="password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <button className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors">
                Update Password
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
